/**
 * MaterialEditModal — universal teacher-only inline editor for any material
 * (Grammatik, Sprechen, Schreiben…). Lets the teacher fix a title, description
 * or swap the level assignment WITHOUT having to delete and re-upload the
 * whole material.
 *
 * Calls PATCH /api/materials/{id} which already accepts title, description,
 * level, levels[]. We deliberately don't expose file editing in the UI yet
 * — that's a bigger flow (re-upload + replace) and the typical fix is a
 * typo in the description, not a wrong file.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Pencil, X, FloppyDisk, Spinner } from '@phosphor-icons/react';
import { isDraftDate, scheduleForSave, DRAFT_HINT } from '../lib/scheduleDraft';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ALL_LEVELS = ['A1', 'A2', 'B1', 'B2', 'C1'];

export default function MaterialEditModal({ material, open, onClose, onSaved }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [levels, setLevels] = useState([]);
  const [scheduledAt, setScheduledAt] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!material) return;
    setTitle(material.title || '');
    setDescription(material.description || material.body || '');
    setLevels(material.levels?.length ? [...material.levels] : (material.level ? [material.level] : ['A2']));
    // `scheduled_at` is now stored as a UTC ISO string; convert back to the
    // teacher's LOCAL time in `datetime-local` shape (YYYY-MM-DDTHH:mm) so
    // what they see in the picker matches what they set (Berlin ≠ UTC).
    const raw = material.scheduled_at || '';
    if (!raw || isDraftDate(raw)) {
      setScheduledAt('');
    } else {
      try {
        const d = new Date(raw);
        // Subtract the timezone offset so the ISO chunk we slice represents
        // local wall-clock time.
        const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
        setScheduledAt(local.toISOString().slice(0, 16));
      } catch {
        setScheduledAt(raw.slice(0, 16));
      }
    }
  }, [material]);

  if (!open || !material) return null;

  const toggleLvl = (l) => setLevels(ls => ls.includes(l) ? ls.filter(x => x !== l) : [...ls, l]);

  const save = async () => {
    if (!title.trim()) return toast.error('Titel fehlt');
    if (levels.length === 0) return toast.error('Mind. 1 Niveau');
    setBusy(true);
    try {
      // Empty string → clears the schedule (backend interprets it as
      // "publish immediately"). A value is sent as-is; the browser gives us
      // local time — append seconds so it's a valid ISO 8601 string.
      // See MaterialsPage: convert local datetime-local → UTC ISO so writes
      // and reads compare against the same clock. Otherwise the teacher's
      // scheduled time was silently treated as UTC.
      const sched = scheduleForSave(scheduledAt, material.scheduled_at);
      const body = { title: title.trim(), description: description.trim(), levels, scheduled_at: sched };
      const res = await axios.patch(`${API}/materials/${material.material_id}`, body, { withCredentials: true });
      toast.success('Material aktualisiert');
      onSaved?.(res.data);
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="material-edit-modal">
      <div className="bg-white rounded-2xl max-w-xl w-full max-h-[90vh] overflow-y-auto p-5 space-y-3" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-heading text-lg font-bold text-[#292524] flex items-center gap-2">
            <Pencil size={18} weight="duotone" className="text-[#AB1C24]" />
            Material bearbeiten
          </h3>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-600" data-testid="material-edit-close">
            <X size={18} weight="bold" />
          </button>
        </div>
        <div className="space-y-2">
          <label className="text-xs font-semibold text-[#57534E] uppercase tracking-wider">Titel *</label>
          <input value={title} onChange={e => setTitle(e.target.value)} className="w-full bg-stone-50 border border-stone-300 rounded px-2 py-1.5 text-sm" data-testid="material-edit-title" />
        </div>
        <div className="space-y-2">
          <label className="text-xs font-semibold text-[#57534E] uppercase tracking-wider">Beschreibung / Aufgabe</label>
          <textarea value={description} onChange={e => setDescription(e.target.value)} rows={6} className="w-full bg-stone-50 border border-stone-300 rounded px-2 py-1.5 text-sm" data-testid="material-edit-description" />
        </div>
        <div className="space-y-2">
          <label className="text-xs font-semibold text-[#57534E] uppercase tracking-wider">Niveaus *</label>
          <div className="flex flex-wrap gap-1.5">
            {ALL_LEVELS.map(l => (
              <button key={l} type="button" onClick={() => toggleLvl(l)}
                className={`px-3 py-1 rounded-full border text-xs font-semibold ${levels.includes(l) ? 'bg-[#AB1C24] border-[#AB1C24] text-white' : 'bg-white border-stone-300 text-[#57534E] hover:border-[#AB1C24]'}`}
                data-testid={`material-edit-level-${l}`}
              >
                {l}
              </button>
            ))}
          </div>
        </div>
        <div className="space-y-1.5">
          <label className="text-xs font-semibold text-[#57534E] uppercase tracking-wider">Veröffentlichungsdatum (optional)</label>
          <input
            type="datetime-local"
            value={scheduledAt}
            onChange={e => setScheduledAt(e.target.value)}
            className="w-full bg-stone-50 border border-stone-300 rounded px-2 py-1.5 text-sm"
            data-testid="material-edit-scheduled-at"
          />
          <p className="text-[11px] text-[#78716C] leading-snug">
            {isDraftDate(material.scheduled_at) ? DRAFT_HINT : 'Leer lassen = sofort sichtbar. Ein zukünftiges Datum blendet das Material für Schüler aus, bis der Zeitpunkt erreicht ist.'}
          </p>
        </div>
        <p className="text-[11px] text-[#A8A29E] italic">
          Möchtest du die Datei ersetzen? Lösche das Material und lade es neu hoch.
        </p>
        <div className="flex gap-2 pt-2 border-t border-stone-200">
          <Button onClick={onClose} variant="outline" className="flex-1" data-testid="material-edit-cancel">Abbrechen</Button>
          <Button onClick={save} disabled={busy} className="flex-1 bg-[#AB1C24] hover:bg-[#8A151C] text-white" data-testid="material-edit-save">
            {busy ? <Spinner size={13} className="animate-spin mr-2" /> : <FloppyDisk size={13} weight="fill" className="mr-2" />}
            Speichern
          </Button>
        </div>
      </div>
    </div>
  );
}
